<script setup lang="ts">
/**
 * Dashboard de la cuenta.
 *
 * Reemplaza la grilla del CRM de Materio, que estaba entera con **datos
 * ficticios** —ingresos, ventas de la semana, crecimiento, reuniones— puestos
 * para ver el diseño (§17.6.8). Un panel de gestión con números inventados es
 * peor que uno vacío: no se puede distinguir de un vistazo qué es real, y a la
 * segunda vez que alguien lo comprueba deja de mirar el panel entero.
 *
 * Lo que quedó sale **todo** de datos que el backend ya expone. Lo que no
 * existe se descartó en vez de simularse: no hay ingresos porque no hay
 * facturación, ni visitas ni conversiones porque no hay analítica, ni series
 * históricas de ningún tipo — así que tampoco hay porcentajes de variación,
 * que son la parte más fácil de inventar y la más difícil de detectar.
 *
 * **La prioridad es lo que viene.** La pregunta con la que alguien abre el
 * portal a la mañana es "¿qué sale hoy y quién viene?", así que las próximas
 * actividades encabezan la fila y los dos gráficos la acompañan.
 *
 * Los tres bloques salen de tarjetas de la plantilla: `CrmMeetingSchedule` (la
 * lista con avatar, fecha y chip), `CrmSalesOverview` (el anillo con su leyenda)
 * y el progreso circular por fila de `Assignment progress`.
 */
import { useTheme } from 'vuetify'
import { hexToRgb } from '@core/utils/colorConverter'
import { listEvents } from '@/api/events'
import { listClients } from '@/api/clients'
import { listOfferings } from '@/api/offerings'
import { ApiError } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import { usePreferences } from '@/composables/usePreferences'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { useDiveCentersStore } from '@/stores/diveCenters'
import { eventIcon, hasEnded, isOverCapacity, occupancyColor, occupancyLabel } from '@/composables/useCalendarEvents'
import { serviceColor } from '@/data/serviceColors'
import { labelForOffering } from '@/data/offerings'
import type { CalendarEvent } from '@/types'

definePage({ meta: { requiresAuth: true } })

const { formatDate } = usePreferences()
const { currentId } = useCurrentDiveCenter()
const diveCentersStore = useDiveCentersStore()

const loading = ref(true)
const error = ref('')

/*
 * Qué centro se muestra lo manda el selector de la cabecera, igual que el menú
 * lateral. Antes esta pantalla tenía su propio desplegable: dos controles para
 * lo mismo, que además podían discrepar.
 */
const centerId = computed(() => currentId.value)

const events = ref<CalendarEvent[]>([])
const clientCount = ref(0)
const offeringCount = ref(0)

/*
 * Ventana que se pide: un mes.
 *
 * El listado muestra las próximas seis y las métricas miran los próximos siete
 * días, pero se trae un mes de una sola vez: con menos, una semana floja deja
 * el bloque principal vacío teniendo salidas cargadas diez días después, que es
 * justo lo que alguien viene a ver.
 */
const HORIZON_DAYS = 30
const METRIC_DAYS = 7
const UPCOMING_LIMIT = 6

const isoDay = (offsetDays = 0) => {
  const d = new Date()

  d.setDate(d.getDate() + offsetDays)

  return d.toISOString().slice(0, 10)
}

/** Lo que todavía no terminó, en orden cronológico. */
const upcoming = computed(() =>
  events.value
    .filter(e => e.status !== 'cancelled' && !hasEnded(e))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
)

/** Recorte de los próximos siete días, que es lo que cuentan las métricas. */
const nextWeek = computed(() => {
  const limit = isoDay(METRIC_DAYS)

  return upcoming.value.filter(e => e.date <= limit)
})

const seatsBooked = computed(() => nextWeek.value.reduce((acc, e) => acc + e.booked, 0))

const widgets = computed(() => [
  {
    title: `Activities · next ${METRIC_DAYS} days`,
    value: nextWeek.value.length,
    icon: 'ri-calendar-check-line',
  },
  {
    title: `Divers booked · next ${METRIC_DAYS} days`,
    value: seatsBooked.value,
    icon: 'ri-group-line',
  },
  {
    title: 'Clients',
    value: clientCount.value,
    icon: 'ri-contacts-book-line',
  },
  {
    title: 'Active offerings',
    value: offeringCount.value,
    icon: 'ri-shopping-bag-3-line',
  },
])

// ---- Por servicio --------------------------------------------------------

/**
 * Las actividades que vienen, agrupadas por **servicio vinculado**.
 *
 * Por servicio y no por categoría: "Deep diving" y "Day boat trips" son cosas
 * distintas de organizar aunque las dos caigan en Diving activities, y agrupar
 * por categoría las fundía en un solo gajo justo donde está la decisión. Es
 * además la misma unidad que la leyenda del calendario, así que los dos se leen
 * sin traducir.
 *
 * El color sale de `serviceColor`, el mismo que pinta sus bloques en la grilla.
 */
const SERVICE_NONE = '__none__'

const byService = computed(() => {
  const map = new Map<string, {
    key: string
    label: string
    color: string
    count: number
    booked: number
    capacity: number
    /** Alguna de sus actividades no declara cupo. */
    unlimited: boolean
  }>()

  for (const e of upcoming.value) {
    const key = e.taxonomyCode || SERVICE_NONE
    const found = map.get(key) ?? {
      key,
      label: key === SERVICE_NONE ? 'No linked service' : labelForOffering(key),
      color: serviceColor(e.taxonomyCode),
      count: 0,
      booked: 0,
      capacity: 0,
      unlimited: false,
    }

    found.count += 1
    found.booked += e.booked

    if (e.capacity == null)
      found.unlimited = true
    else
      found.capacity += e.capacity

    map.set(key, found)
  }

  return [...map.values()].sort((a, b) => b.count - a.count)
})

const donutSeries = computed(() => byService.value.map(c => c.count))
const donutLabels = computed(() => byService.value.map(c => c.label))
const donutColors = computed(() => byService.value.map(c => c.color))

const vuetifyTheme = useTheme()

/*
 * Opciones del anillo, portadas de `CrmSalesOverview`: `sparkline`, sin leyenda
 * propia —la de al lado lleva los números— y el total en el centro. Los colores
 * no son los de la plantilla sino los del servicio, por el motivo de arriba.
 */
const donutOptions = computed(() => {
  const colors = vuetifyTheme.current.value.colors
  const variables = vuetifyTheme.current.value.variables

  const secondaryTextColor = `rgba(${hexToRgb(colors['on-surface'])},${variables['medium-emphasis-opacity']})`
  const primaryTextColor = `rgba(${hexToRgb(colors['on-surface'])},${variables['high-emphasis-opacity']})`

  return {
    chart: { sparkline: { enabled: true } },
    colors: donutColors.value,
    labels: donutLabels.value,
    stroke: { width: 0 },
    legend: { show: false },
    dataLabels: { enabled: false },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    plotOptions: {
      pie: {
        customScale: 0.9,
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: { offsetY: 25, color: secondaryTextColor },
            value: {
              offsetY: -15,
              fontWeight: 500,
              fontSize: '24px',
              color: primaryTextColor,
              formatter: (value: unknown) => `${value}`,
            },
            total: {
              show: true,
              label: 'Activities',
              fontSize: '15px',
              color: secondaryTextColor,
              formatter: (value: { globals: { seriesTotals: number[] } }) =>
                `${value.globals.seriesTotals.reduce((t, n) => t + n, 0)}`,
            },
          },
        },
      },
    },
  }
})

/**
 * Escala del círculo cuando lo que se muestra es una CANTIDAD y no un
 * porcentaje: 10, y si se pasa, la potencia de diez siguiente.
 *
 * Un porcentaje tiene su tope natural en 100; una cantidad no, y sin escala el
 * círculo no puede dibujar nada. Redondear al orden de magnitud mantiene la
 * comparación entre filas honesta —dos categorías con 3 y 7 se ven distintas—
 * sin fingir un máximo que nadie declaró.
 */
function scaleFor(value: number): number {
  if (value <= 10)
    return 10

  return 10 ** Math.ceil(Math.log10(value))
}

/**
 * Ocupación por servicio.
 *
 * Con cupo declarado es un porcentaje —lo que realmente se quiere saber: cuánto
 * queda por vender—. Sin cupo no hay porcentaje posible, así que se muestra la
 * cantidad de anotados: inventar un denominador sería inventar el dato.
 */
const occupancyByService = computed(() =>
  byService.value.map(c => {
    const hasLimit = c.capacity > 0 && !c.unlimited
    const percent = hasLimit ? Math.round((c.booked / c.capacity) * 100) : 0

    return {
      ...c,
      hasLimit,
      percent,
      // Con cupo, el círculo es el porcentaje; sin cupo, la cantidad sobre su
      // escala.
      ring: hasLimit ? Math.min(100, percent) : (c.booked / scaleFor(c.booked)) * 100,
      display: hasLimit ? `${percent}%` : String(c.booked),
      caption: hasLimit
        ? `${c.booked} of ${c.capacity} seats · ${c.count} activities`
        : `${c.booked} booked · ${c.count} activities · no limit set`,
    }
  }),
)

/**
 * Lo que pide una decisión antes de que salga.
 *
 * Se limita a lo que el propio calendario ya sabe y a lo que tiene arreglo: sin
 * equipo asignado, pasada de cupo, o una salida que el horario dejó de producir
 * pero tiene gente anotada. No se inventa una lista de "sugerencias": si no hay
 * nada, es una buena noticia y se dice así.
 */
const attention = computed(() =>
  nextWeek.value
    .map(e => {
      if (e.detached)
        return { event: e, label: 'No longer produced by your schedule', color: 'warning' }

      if (isOverCapacity(e))
        return { event: e, label: `Over capacity — ${occupancyLabel(e)}`, color: 'error' }

      if (!e.staff.length && e.booked > 0)
        return { event: e, label: 'Nobody from the team assigned', color: 'warning' }

      return null
    })
    .filter(Boolean) as { event: CalendarEvent; label: string; color: string }[],
)

/**
 * Lo pendiente, **agrupado por día** y en orden de salida.
 *
 * Antes se repartía en columnas de a seis, con el ancho calculado a partir de
 * cuántas columnas salieran. Con una docena de avisos todavía funcionaba; con
 * treinta —el día que se reescribe un horario y media semana queda huérfana—
 * daban seis columnas de `md=2`, y a ese ancho un título como "Day boat trips
 * (1/2/3-tank)" se parte carácter por carácter. Cada alerta tomaba además un
 * alto distinto según cuánto se hubiera partido su texto, así que el bloque se
 * veía como un mosaico roto justo cuando más hay que leerlo.
 *
 * Ahora el ancho es FIJO —tres por fila— y lo que crece es el alto, que es la
 * dirección en la que una página puede crecer sin romperse. El día pasa a ser un
 * encabezado en vez de repetirse en cada tarjeta: con los avisos ya agrupados,
 * la fecha en cada uno era ruido, y sacarla deja el renglón para la hora y el
 * título, que es lo que distingue un aviso del de al lado.
 */
interface AttentionDay {
  date: string
  items: { event: CalendarEvent; label: string; color: string }[]
}

const attentionDays = computed<AttentionDay[]>(() => {
  const byDate = new Map<string, AttentionDay['items']>()

  for (const a of attention.value) {
    const found = byDate.get(a.event.date)

    if (found)
      found.push(a)
    else
      byDate.set(a.event.date, [a])
  }

  return [...byDate.entries()]
    // Las fechas son 'YYYY-MM-DD', así que ordenan como cadenas.
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({
      date,
      // Dentro del día, por hora: es el orden en que hay que resolverlos.
      items: [...items].sort((x, y) => x.event.time.localeCompare(y.event.time)),
    }))
})

/*
 * Los destinos van como RUTA EN TEXTO y no como `{ name, params }`.
 *
 * `RouterLink` con rutas tipadas (unplugin-vue-router) exige el nombre literal
 * del mapa generado, y un destino armado en un `computed` llega como unión y no
 * encaja. La cadena sí, es lo mismo que ya se hace en el resto del portal, y de
 * paso el enlace se puede abrir en otra pestaña.
 */
const calendarTo = computed(() =>
  centerId.value ? `/dive-centers/${centerId.value}/calendar` : '/dive-centers')

/**
 * Ruta a una actividad concreta del calendario.
 *
 * Lleva la fecha **y** el id: la fecha para que el calendario abra en la semana
 * correcta —una salida de dentro de tres semanas no está en el rango que se
 * carga por defecto— y el id para que abra su panel. Sin la fecha, el enlace
 * dejaría al usuario en la semana actual buscando a mano lo que acaba de tocar.
 */
const eventTo = (e: CalendarEvent) =>
  centerId.value
    ? `/dive-centers/${centerId.value}/calendar?date=${e.date}&event=${e.id}`
    : calendarTo.value

async function loadCenterData(id: string | null) {
  events.value = []
  offeringCount.value = 0

  if (!id)
    return

  const [ev, off] = await Promise.allSettled([
    listEvents(id, { from: isoDay(), to: isoDay(HORIZON_DAYS) }),
    listOfferings(id),
  ])

  if (ev.status === 'fulfilled')
    events.value = ev.value.events

  if (off.status === 'fulfilled')
    offeringCount.value = off.value.offerings.filter(o => o.active).length
}

onMounted(async () => {
  try {
    // La lista sale del store compartido —el layout ya la pidió al montar— y
    // `load()` se cuelga de esa misma llamada en vez de repetirla.
    await diveCentersStore.load()

    // El total de clientes es de la CUENTA, no del centro: se pide una vez y
    // con `limit: 1`, porque de la respuesta solo interesa el total.
    const [clients] = await Promise.allSettled([listClients({ limit: 1 })])

    if (clients.status === 'fulfilled')
      clientCount.value = clients.value.total

    await loadCenterData(centerId.value)
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load the dashboard.'
  }
  finally {
    loading.value = false
  }
})

/*
 * El Dashboard no lleva `:id` en la URL, así que el `:key` del `RouterView`
 * —atado al id de la ruta— no lo remonta al cambiar de centro desde la
 * cabecera. Sin este watcher se quedaría mostrando las salidas del anterior.
 */
watch(centerId, async (id, previous) => {
  // El primer valor lo trae `onMounted`, que ya carga: sin esto se pediría dos
  // veces al entrar.
  if (!previous)
    return

  loading.value = true
  await loadCenterData(id)
  loading.value = false
})
</script>

<template>
  <section>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-6"
    />

    <!-- Cuenta recién creada: una sola cosa que hacer, y dicha. -->
    <VCard
      v-else-if="!diveCentersStore.list.length"
      class="text-center pa-10"
    >
      <VIcon
        icon="ri-anchor-line"
        size="48"
        color="primary"
        class="mb-4"
      />
      <h5 class="text-h5 mb-2">
        Add your dive center
      </h5>
      <p class="text-body-1 text-medium-emphasis mb-6">
        Everything else — offerings, the calendar and your clients — hangs off it.
      </p>
      <VBtn
        color="primary"
        prepend-icon="ri-add-line"
        :to="{ name: 'dive-centers-new' }"
      >
        Add dive center
      </VBtn>
    </VCard>

    <template v-else>
      <!-- Tira de métricas, el mismo bloque que el listado de clientes. -->
      <VCard class="mb-6">
        <VCardText>
          <VRow>
            <template
              v-for="(w, index) in widgets"
              :key="w.title"
            >
              <VCol
                cols="12"
                sm="6"
                md="3"
                class="px-6"
              >
                <div
                  class="d-flex justify-space-between"
                  :class="$vuetify.display.xs
                    ? index !== widgets.length - 1 ? 'border-b pb-4' : ''
                    : $vuetify.display.sm
                      ? index < (widgets.length / 2) ? 'border-b pb-4' : ''
                      : ''"
                >
                  <div class="d-flex flex-column gap-y-1">
                    <h4 class="text-h4">
                      {{ w.value }}
                    </h4>
                    <span class="text-base">{{ w.title }}</span>
                  </div>

                  <VAvatar
                    variant="tonal"
                    rounded
                    size="42"
                  >
                    <VIcon
                      :icon="w.icon"
                      size="26"
                    />
                  </VAvatar>
                </div>
              </VCol>
              <VDivider
                v-if="$vuetify.display.mdAndUp ? index !== widgets.length - 1
                  : $vuetify.display.smAndUp ? index % 2 === 0
                    : false"
                vertical
                inset
                length="60"
              />
            </template>
          </VRow>
        </VCardText>
      </VCard>

      <!--
        Lo pendiente va PRIMERO, encima de todo lo demás: es lo único del panel
        que exige hacer algo, y debajo de tres tarjetas informativas se
        encontraba recién después de bajar.
      -->
      <VCard class="mb-6">
        <VCardItem>
          <VCardTitle>Needs attention</VCardTitle>
          <VCardSubtitle>Next {{ METRIC_DAYS }} days</VCardSubtitle>
        </VCardItem>

        <VCardText>
          <!-- Sin nada pendiente es una buena noticia, y se dice: un bloque
               vacío se lee como que algo no cargó. -->
          <div
            v-if="!attention.length"
            class="d-flex align-center ga-3 text-body-2 text-medium-emphasis"
          >
            <VIcon
              icon="ri-checkbox-circle-line"
              color="success"
            />
            Nothing to sort out before these activities run.
          </div>

          <!--
            El `v-for` va DENTRO de un `<template v-else>` y no sobre el mismo
            div: en Vue 3 `v-if` gana prioridad sobre `v-for`, así que la
            combinación funciona, pero se lee al revés de como se ejecuta y el
            linter la marca. El template deja el corte de la rama separado del
            recorrido de la lista.
          -->
          <template v-else>
            <div
              v-for="day in attentionDays"
              :key="day.date"
              class="mb-5"
            >
              <div class="text-overline text-medium-emphasis mb-2">
                {{ formatDate(day.date) }}
              </div>

              <VRow dense>
                <VCol
                  v-for="a in day.items"
                  :key="a.event.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <!--
                    Cada aviso abre su actividad, igual que las filas de arriba:
                    el aviso dice qué hay que resolver, y resolverlo siempre es
                    entrar a esa salida. Va como enlace y no como botón para que
                    se pueda abrir en otra pestaña.

                    `h-100` en las dos capas: sin eso, en una fila de tres el que
                    tiene el título más largo se estira y los otros dos quedan
                    cortos, que es la mitad del efecto de mosaico roto.
                  -->
                  <RouterLink
                    :to="eventTo(a.event)"
                    class="bt-attention h-100"
                  >
                    <VAlert
                      :type="a.color === 'error' ? 'error' : 'warning'"
                      variant="tonal"
                      density="compact"
                      class="h-100"
                    >
                      <div class="text-body-2 font-weight-medium">
                        {{ a.event.time }} · {{ a.event.title }}
                      </div>
                      <div class="text-caption">
                        {{ a.label }}
                      </div>
                    </VAlert>
                  </RouterLink>
                </VCol>
              </VRow>
            </div>
          </template>
        </VCardText>
      </VCard>

      <VRow class="match-height">
        <!-- El bloque principal: qué sale y quién viene. -->
        <VCol
          cols="12"
          md="5"
        >
          <VCard>
            <VCardItem>
              <VCardTitle>Upcoming activities</VCardTitle>
              <template #append>
                <!--
                  El botón lleva el nombre de la sección a la que va, no el del
                  widget que la dibuja: quien lo lee tiene que reconocer dónde
                  va a caer, y en el menú se llama así.
                -->
                <VBtn
                  variant="text"
                  size="small"
                  append-icon="ri-arrow-right-line"
                  :to="calendarTo"
                >
                  Plans &amp; Bookings
                </VBtn>
              </template>
            </VCardItem>

            <VCardText>
              <p
                v-if="!upcoming.length"
                class="text-body-2 text-medium-emphasis mb-0"
              >
                Nothing scheduled in the next {{ HORIZON_DAYS }} days.
              </p>

              <VList
                v-else
                lines="two"
                class="card-list"
              >
                <!-- Cada fila entra al calendario Y abre el panel de esa
                     actividad: el gesto ya dijo cuál. -->
                <VListItem
                  v-for="e in upcoming.slice(0, UPCOMING_LIMIT)"
                  :key="e.id"
                  :to="eventTo(e)"
                >
                  <template #prepend>
                    <VAvatar
                      :size="38"
                      variant="tonal"
                      :style="{ color: serviceColor(e.taxonomyCode) }"
                    >
                      <VIcon :icon="eventIcon(e.taxonomyCode)" />
                    </VAvatar>
                  </template>

                  <VListItemTitle class="font-weight-medium mb-1">
                    {{ e.title }}
                  </VListItemTitle>
                  <VListItemSubtitle class="me-2">
                    <VIcon
                      size="16"
                      start
                      icon="ri-calendar-line"
                    />
                    {{ formatDate(e.date) }} | {{ e.time }}
                  </VListItemSubtitle>

                  <template #append>
                    <VListItemAction>
                      <VChip
                        size="small"
                        :color="occupancyColor(e)"
                      >
                        {{ occupancyLabel(e) || 'No limit' }}
                      </VChip>
                    </VListItemAction>
                  </template>
                </VListItem>
              </VList>
            </VCardText>
          </VCard>
        </VCol>

        <!-- En qué se le va el mes al centro. -->
        <VCol
          cols="12"
          md="3"
        >
          <VCard>
            <VCardItem>
              <VCardTitle>By service</VCardTitle>
              <VCardSubtitle>Next {{ HORIZON_DAYS }} days</VCardSubtitle>
            </VCardItem>

            <VCardText>
              <p
                v-if="!byService.length"
                class="text-body-2 text-medium-emphasis mb-0"
              >
                Nothing scheduled yet.
              </p>

              <template v-else>
                <div class="mx-auto">
                  <VueApexCharts
                    type="donut"
                    :options="donutOptions"
                    :series="donutSeries"
                    :height="200"
                    :width="200"
                  />
                </div>

                <VDivider class="my-4" />

                <div
                  v-for="c in byService"
                  :key="c.key"
                  class="d-flex align-center justify-space-between mb-2"
                >
                  <div class="d-flex align-center ga-2 text-truncate">
                    <span
                      class="bt-dot"
                      :style="{ backgroundColor: c.color }"
                    />
                    <span class="text-body-2 text-truncate">{{ c.label }}</span>
                  </div>
                  <span class="text-body-2 font-weight-medium">{{ c.count }}</span>
                </div>
              </template>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Cuánto queda por vender, por categoría. -->
        <VCol
          cols="12"
          md="4"
        >
          <VCard>
            <VCardItem>
              <VCardTitle>Occupancy by service</VCardTitle>
              <VCardSubtitle>Next {{ HORIZON_DAYS }} days</VCardSubtitle>
            </VCardItem>

            <VCardText>
              <p
                v-if="!occupancyByService.length"
                class="text-body-2 text-medium-emphasis mb-0"
              >
                Nothing scheduled yet.
              </p>

              <div
                v-for="c in occupancyByService"
                :key="c.key"
                class="d-flex align-center ga-4 mb-4"
              >
                <VProgressCircular
                  :model-value="c.ring"
                  :size="52"
                  :width="5"
                  :color="c.color"
                >
                  <span class="text-caption font-weight-medium">{{ c.display }}</span>
                </VProgressCircular>

                <div class="text-truncate">
                  <div class="text-body-1 font-weight-medium text-truncate">
                    {{ c.label }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ c.caption }}
                  </div>
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

      </VRow>
    </template>
  </section>
</template>

<style lang="scss" scoped>
// El espaciado de `CrmMeetingSchedule`, que es de donde sale esta lista.
.card-list {
  --v-card-list-gap: 1.5rem;
}

/* El aviso entero es el enlace: el color y el texto los pone la alerta. */
.bt-attention {
  display: block;
  color: inherit;
  text-decoration: none;
}

.bt-dot {
  display: inline-block;
  flex-shrink: 0;
  border-radius: 50%;
  block-size: 10px;
  inline-size: 10px;
}
</style>
